import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Button } from "@/components/ui/button";
import { useAccountStore } from "@/store/accountStore";
import { useAuthStore } from "@/store/authStore";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const accountId = user?.currentAccountId ?? user?.ownedAccountId ?? "";
  const isOwner = user?.ownedAccountId === accountId;

  const { account, isLoading, isSaving, error, fetchAccount, updateAccount } = useAccountStore();

  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (accountId) {
      fetchAccount(accountId);
    }
  }, [accountId, fetchAccount]);

  const [nameSource, setNameSource] = useState<typeof account>(null);
  if (account !== nameSource) {
    setNameSource(account);
    if (account) setNameValue(account.name);
  }

  const handleEdit = () => {
    setEditing(true);
    setSaveError(null);
    setSaved(false);
  };

  const handleCancel = () => {
    setEditing(false);
    setNameValue(account?.name ?? "");
    setSaveError(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = nameValue.trim();
    if (!trimmed) {
      return;
    }
    setSaveError(null);
    try {
      await updateAccount(accountId, { name: trimmed });
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    }
  };

  return (
    <ProtectedRoute>
      <div className="lw-settings container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="lw-settings-header mb-8">
            <h1 className="text-3xl font-bold text-foreground">Settings</h1>
            <p className="mt-1 text-sm text-muted-foreground">Manage your account configuration</p>
          </div>

          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
              Loading…
            </div>
          )}

          {error && !isLoading && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {account && !isLoading && (
            <div className="lw-settings-card rounded-lg border border-border bg-card">
              <div className="px-6 py-4 border-b border-border">
                <h2 className="text-base font-semibold text-foreground">Account</h2>
              </div>

              <div className="divide-y divide-border">
                {/* Account name */}
                <div className="px-6 py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Account name
                      </label>
                      {editing ? (
                        <form onSubmit={handleSave} className="flex items-center gap-3">
                          <input
                            autoFocus
                            value={nameValue}
                            onChange={(e) => setNameValue(e.target.value)}
                            maxLength={200}
                            className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                          <Button type="submit" disabled={isSaving || !nameValue.trim()}>
                            {isSaving ? "Saving…" : "Save"}
                          </Button>
                          <button
                            type="button"
                            onClick={handleCancel}
                            className="rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
                          >
                            Cancel
                          </button>
                        </form>
                      ) : (
                        <p className="text-sm text-foreground">{account.name}</p>
                      )}
                      {saveError && (
                        <p className="mt-1.5 text-xs text-destructive">{saveError}</p>
                      )}
                      {saved && (
                        <p className="mt-1.5 text-xs text-green-600">Saved</p>
                      )}
                    </div>
                    {!editing && isOwner && (
                      <button
                        onClick={handleEdit}
                        className="shrink-0 text-sm text-primary hover:underline"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>

                {/* Account ID */}
                <div className="px-6 py-5">
                  <p className="text-sm font-medium text-foreground mb-1">Account ID</p>
                  <p className="text-sm font-mono text-muted-foreground break-all">{account.id}</p>
                </div>

                {/* Owner */}
                <div className="px-6 py-5">
                  <p className="text-sm font-medium text-foreground mb-1">Owner ID</p>
                  <p className="text-sm font-mono text-muted-foreground break-all">{account.ownerId}</p>
                </div>

                {/* Dates */}
                <div className="px-6 py-5 grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">Created</p>
                    <p className="text-sm text-muted-foreground">{formatDate(account.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">Last updated</p>
                    <p className="text-sm text-muted-foreground">{formatDate(account.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ProtectedRoute>
  );
}
